import { defineStore } from 'pinia'
import axios from 'axios'
import { useProductOrderStore } from '@/features/productOrder/ProductOrder.store'
import { getFormatString, getPropositions, getTheme } from '@/api/api'
import { getMessages } from '@/api/messagesApi'
import { useAddressBookStore } from '@/stores/AddressBook.store'
import { useFileListStore } from '@/stores/FileList.store'
import { useFileStoreStore } from '@/features/filestore/FileStore.store'
import { usePrintOrderStore } from '@/features/printOrder/PrintOrder.store'
import { usePrintStoreStore } from '@/features/printstore/PrintStore.store'
import { usePriceTableStore } from '@/features/printstore/PriceTable.store'
import { EventBus, Events } from '@/eventbus/EventBus'

export const UPLOAD_COOKIE = 'Rijnja-Upload'

export const useRootStore = defineStore('root', {
  state: () => ({
    versionUuid: undefined,
    backgrounds: [],
    buttons: {},
    fullscreen: false,
    globe: {
      hideMenu: true,
      subClosed: true,
      showHome: true,
      buttonBackgroundColor: ''
    },
    interaction: {},
    interactions: [],
    // false zolang de propositions nog niet zijn opgehaald. GlobeMenu wacht
    // hierop, anders rendert die eerst de UploadGlobe en springt daarna naar de
    // CartGlobe zodra de interactions binnen zijn (zichtbare blink).
    propositionsLoaded: false,
    isNavOpen: false,
    loggedIn: false,
    theme: {
      customFields: {
        project: {
          required: false
        },
        addresses: {},
        department: {},
        reference: {
          required: false
        },
        externalId: {
          show: false,
          required: false
        },
        printStore: {
          hidePricing: false,
          hideOwnPricing: false
        }
      },
      header: {
        menuRight: {}
      },
      hideChangePw: false,
      hideLogOut: false
    },
    user: {},
    currentSection: '',
    parentRoute: '',
    preferences: {
      showFileStoreIntro: true,
      showPrintStoreAd: true,
      showPrintIntro: true,
      showCopyIntro: true,
      showScanIntro: true,
      showCovidIntro: true,
      showCovidMenuIntro: true,
      showCopyScanIntro: true,
      showRepeatNotification: true,
      showPrintStorePopUp: true
    },
    dragging: false,
    uploadProgress: 0,
    uploadInProgress: false,
    windowInnerHeight: 0,
    mobile: false,
    messages: [],
    redirectAfterLogin: undefined
  }),
  persist: {
    storage: localStorage,
    pick: ['preferences']
  },
  getters: {
    isMobile: (state) => state.mobile,
    isLoggedIn: (state) => state.loggedIn
  },
  actions: {
    setRedirectAfterLogin(val) {
      this.redirectAfterLogin = val
    },
    hydrateMessages(val) {
      this.messages = val
    },
    setFullscreen(val) {
      this.fullscreen = val
    },
    hideFileStoreIntro(val) {
      this.preferences.showFileStoreIntro = val
    },
    setPreference(key, value) {
      this.preferences[key] = value
    },
    globeReset() {
      this.globe.hideMenu = false
      this.globe.subClosed = true
      Object.assign(this.globe, this.theme.globe)
    },
    globeSetSub(val) {
      this.globe.subClosed = !val
    },
    globeSetMain(val) {
      this.globe.hideMenu = !val
    },
    setParentRoute(val) {
      this.parentRoute = val
    },
    globePlain() {
      this.globe.hideMenu = true
      this.globe.subClosed = true
    },
    setDefaultProject(data) {
      if (!this.user) this.user = {}
      this.user.defaultProjectId = data
    },
    setInteractions(data) {
      let interactions = []
      this.backgrounds = []
      this.interactions = []
      const userRoles = this.loggedIn && this.user.roles ? this.user.roles : []
      for (var i = this.theme.propositions.length; i--; ) {
        const proposition = this.theme.propositions[i]
        let requiredRole = proposition.requiredUserRole || ''
        if (proposition.destinationType === 'interaction') {
          if (!proposition.mainColor)
            proposition.mainColor = this.theme.mainColor
          if (!proposition.subColor) proposition.subColor = this.theme.subColor
          if (!proposition.errorColor)
            proposition.errorColor = this.theme.errorColor
          if (!proposition.actionColor)
            proposition.actionColor = this.theme.actionColor
          proposition.id = proposition.pid
          interactions.push(proposition)
        } else if (proposition.destinationType === 'menu') {
          proposition.menuItems.forEach((menuItem, index) => {
            requiredRole = data[menuItem.pid].requiredUserRole
            if (requiredRole && userRoles.indexOf(requiredRole) < 0) {
              this.theme.propositions[i].menuItems.splice(index, 1)
            } else if (menuItem.destinationType === 'interaction') {
              data[menuItem.pid].mainColor =
                proposition.mainColor || this.theme.mainColor
              data[menuItem.pid].subColor =
                proposition.subColor || this.theme.subColor
              data[menuItem.pid].errorColor =
                proposition.errorColor || this.theme.errorColor
              data[menuItem.pid].selectionColor = proposition.selectionColor
              data[menuItem.pid].actionColor =
                proposition.actionColor || this.theme.actionColor
              data[menuItem.pid].backgroundImage =
                proposition.backgroundImage || this.theme.backgroundImage
              data[menuItem.pid].backgroundPosX =
                proposition.backgroundPosX || this.theme.backgroundPosX
              data[menuItem.pid].backgroundPosY =
                proposition.backgroundPosY || this.theme.backgroundPosY
              data[menuItem.pid].backgroundColor = proposition.backgroundColor
              data[menuItem.pid].propTitleColor = proposition.propTitleColor
              data[menuItem.pid].printStoreCategory =
                proposition.printStoreCategory || ''
              data[menuItem.pid].printStoreCollection =
                proposition.printStoreCollection || ''
              interactions.push(data[menuItem.pid])
            }
          })
        }
        if (
          proposition.backgroundImage &&
          this.backgrounds.indexOf(proposition.backgroundImage) < 0
        ) {
          this.backgrounds.push(proposition.backgroundImage)
        }
      }
      this.interactions = interactions
    },
    setHome() {
      document.body.style.setProperty('--main-color', this.theme.mainColor)
      document.body.style.setProperty('--sub-color', this.theme.subColor)
      document.body.style.setProperty(
        '--error-color',
        this.theme.errorColor || '#fd514d'
      )
      document.body.style.setProperty('--action-color', this.theme.actionColor)
      this.buttons = {
        pillDefault: {
          'background-color': this.theme.subColor
        },
        pillSecondary: {
          color: this.theme.subColor
        },
        btnDefault: {
          //color: this.theme.subColor
        },
        btnActive: {
          'background-color': this.theme.subColor,
          color: 'white'
        }
      }
      this.globe.hideMenu = false
      this.globe.subClosed = true
      this.interaction = {}
      Object.assign(this.globe, this.theme.globe)
    },
    setInteraction(prop = 0) {
      // pid kan uit het CMS als string komen ('18'), id is altijd een getal
      let iaToGo =
        this.interactions.find((p) => String(p.id) === String(prop.pid)) || {}
      this.interaction = iaToGo
      if (iaToGo.propCustomSpecs) {
        useProductOrderStore().hydrateProducts(
          iaToGo.propCustomSpecs,
          false,
          true
        )
      } else {
        useProductOrderStore().propositonProducts = []
      }
      document.body.style.setProperty(
        '--arch-color',
        this.theme.archColor ||
          this.interaction.backgroundColor ||
          this.interaction.mainColor ||
          this.theme.mainColor
      )
      document.body.style.setProperty(
        '--arch-text-color',
        this.interaction.propTitleColor || 'white'
      )
      document.body.style.setProperty(
        '--main-color',
        this.interaction.mainColor || this.theme.mainColor
      )
      document.body.style.setProperty(
        '--sub-color',
        this.interaction.subColor || this.theme.subColor
      )
      document.body.style.setProperty(
        '--error-color',
        this.interaction.errorColor || this.theme.errorColor || '#fd514d'
      )
      document.body.style.setProperty(
        '--action-color',
        this.interaction.actionColor || this.theme.actionColor || '#ff9800'
      )
      const color =
        this.interaction.backgroundColor ||
        this.interaction.mainColor ||
        this.theme.mainColor
      function hexToRgb(hex) {
        var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
        return result
          ? {
              r: parseInt(result[1], 16),
              g: parseInt(result[2], 16),
              b: parseInt(result[3], 16)
            }
          : null
      }
      let selColor = this.interaction.selectionColor
        ? this.interaction.selectionColor
        : 'rgba(' +
          hexToRgb(color).r +
          ',' +
          hexToRgb(color).g +
          ',' +
          hexToRgb(color).b +
          ',0.20)'
      document.body.style.setProperty('--selected-color', selColor)
      this.buttons = {
        pillDefault: {
          'background-color': this.interaction.subColor || this.theme.subColor
        },
        pillSecondary: {
          color: this.interaction.subColor || this.theme.subColor
        },
        btnDefault: {
          //color: this.interaction.subColor || this.theme.subColor
        },
        btnActive: {
          'background-color': this.interaction.subColor || this.theme.subColor,
          color: 'white'
        }
      }
      Object.assign(this.globe, iaToGo.globe)
    },
    setTheme(theme) {
      if (theme.customFields.project.useFormatString) {
        getFormatString(theme.customFields.project.formatString).then(
          (resp) => {
            if (!this.user) this.user = {}
            this.user.formatString = resp
          }
        )
      }
      let props = theme.propositions
      const userRoles = this.loggedIn && this.user.roles ? this.user.roles : []
      const userLevel = this.loggedIn ? this.user.securityLevel : 0
      for (var i = theme.propositions.length; i--; ) {
        const requiredRole = theme.propositions[i].requiredUserRole || ''
        const propLevel = parseInt(theme.propositions[i].securityLevel) || 0
        if (requiredRole && userRoles.indexOf(requiredRole) < 0) {
          theme.propositions.splice(i, 1)
        } else if (userLevel < propLevel) {
          theme.propositions.splice(i, 1)
        }
      }
      theme.propositions.sort(function (a, b) {
        return a.btnPos - b.btnPos
      })
      this.getPropositions()
      if (theme.userRequestedPrivateTheme) {
        EventBus.emit(Events.REQUEST_LOGIN)
      }
      this.theme = theme
      // Tell the API which theme is shown so it can resolve the matching PrintStore
      if (theme.mainThemeName) {
        axios.defaults.headers.common['X-Theme'] = theme.mainThemeName
      } else {
        delete axios.defaults.headers.common['X-Theme']
      }
      if (this.loggedIn) {
        useAddressBookStore().hydrate()
      }
      this.buttons = {
        pillDefault: {
          'background-color': this.theme.subColor
        },
        pillSecondary: {
          color: this.theme.subColor
        },
        btnDefault: {
          color: this.theme.subColor
        },
        btnActive: {
          'background-color': this.interaction.subColor || this.theme.subColor,
          color: 'white'
        }
      }
      if (!this.theme.customFields.addresses)
        this.theme.customFields.addresses = {}
      if (!this.theme.customFields.department)
        this.theme.customFields.department = {}
      if (!this.theme.customFields.printSpecs)
        this.theme.customFields.printSpecs = {}
      if (!this.theme.customFields.printProduct)
        this.theme.customFields.printProduct = {}
      if (!this.theme.customFields.printStore)
        this.theme.customFields.printStore = {}
      if (!this.theme.customFields.printStoreAd)
        this.theme.customFields.printStoreAd = {}
      Object.assign(this.globe, theme.globe)
      document.body.style.setProperty('--main-color', this.theme.mainColor)
      document.body.style.setProperty('--sub-color', this.theme.subColor)
      document.body.style.setProperty(
        '--icon-color',
        this.theme.iconColor || this.theme.subColor
      )
      document.body.style.setProperty('--arch-color', this.theme.archColor)
      document.body.style.setProperty(
        '--error-color',
        this.theme.errorColor || '#fd514d'
      )
      document.body.style.setProperty('--action-color', this.theme.actionColor)
      this.loadMessages(this.theme.mainThemeName)
      if (theme.printStore) {
        if (!this.user) this.user = {}
        this.user.printStore = theme.printStore
        if (this.theme.customFields.printStore.priceTable) {
          usePriceTableStore().hydrate(
            this.theme.customFields.printStore.priceTable
          )
        }
        setTimeout(() => usePrintStoreStore().hydrate(), 3)
      }
      //set custom printspecs
      let customPrintSpecs
      if (
        this.theme.customFields.printSpecs.userPrintSpecs &&
        userRoles.indexOf('USER_PRINTSPECS') > -1
      ) {
        customPrintSpecs = this.theme.customFields.printSpecs.userPrintSpecsFile
      } else if (this.theme.customFields.printSpecs.custom) {
        customPrintSpecs = this.theme.customFields.printSpecs.fileName
      }
      if (customPrintSpecs) {
        usePrintOrderStore().hydratePrintSpecs(customPrintSpecs)
      }

      let customPrintSpecsSecured
      if (this.theme.customFields.printSpecsSecured?.custom) {
        customPrintSpecsSecured = this.theme.customFields.printSpecsSecured
          .fileName
      }
      if (customPrintSpecsSecured) {
        usePrintOrderStore().hydratePrintSpecsSecured(customPrintSpecsSecured)
      }

      //set custom printTemplates
      let customPrintTemplates
      if (
        this.theme.customFields.printProduct.userPrintTemplates &&
        userRoles.indexOf('USER_PRINTSPECS') > -1
      ) {
        customPrintTemplates = this.theme.customFields.printProduct
          .userPrintTemplatesFile
      } else if (
        this.theme.customFields.printProduct.custom &&
        this.theme.customFields.printProduct.fileName
      ) {
        customPrintTemplates = this.theme.customFields.printProduct.fileName
      }
      if (customPrintTemplates) {
        usePrintOrderStore().hydratePrintTemplates(customPrintTemplates)
      }
      //set custom secure printTemplates
      if (
        this.theme.customFields.printProduct.custom &&
        this.theme.customFields.printProduct.securedFileName
      ) {
        let customSecurePrintTemplates = this.theme.customFields.printProduct
          .securedFileName
        usePrintOrderStore().hydratePrintTemplatesSecure(
          customSecurePrintTemplates
        )
      }
      if (
        this.theme.customFields.productOrder?.userRoleProducts &&
        userRoles.indexOf('USER_PRINTSPECS') > -1
      ) {
        useProductOrderStore().hydrateProducts(
          this.theme.customFields.productOrder.fileNameUserRoleProducts,
          false
        )
      } else if (this.theme.customFields.productOrder?.custom) {
        useProductOrderStore().hydrateProducts(
          this.theme.customFields.productOrder.fileName,
          false
        )
      } else if (this.theme.customFields.productOrder?.additionalProducts) {
        useProductOrderStore().hydrateProducts(
          this.theme.customFields.productOrder.fileName,
          true
        )
      }

      if (this.theme.customFields.productOrder?.customSecured) {
        useProductOrderStore().hydrateSecuredProducts(
          this.theme.customFields.productOrder.securedFileName,
          false
        )
      } else if (this.theme.customFields.productOrder?.additionalSecured) {
        useProductOrderStore().hydrateSecuredProducts(
          this.theme.customFields.productOrder.securedFileName,
          true
        )
      }
    },
    setUser(user) {
      this.user = user
      if (
        this.user.notificationPreferences &&
        this.user.notificationPreferences.orderConfirmation === undefined
      ) {
        this.user.notificationPreferences.orderConfirmation = true
      }
      // const { hydrate } = useAddressBookStore()
      // setTimeout(() => hydrate(), 10)
      const fileStoreStore = useFileStoreStore()
      setTimeout(() => fileStoreStore.hydrate(), 20)
      setTimeout(() => {
        const fileListStore = useFileListStore()
        fileListStore.getFiles()
      }, 30)
    },
    setLogin(user = {}) {
      this.loggedIn = true
      this.setUser(user)
    },
    toggleSideBar(val) {
      if (val) {
        document.documentElement.style.overflow = 'hidden'
      } else {
        document.documentElement.style.overflow = 'initial'
      }
      this.isNavOpen = val
    },
    logout() {
      this.user = {}
      this.loggedIn = false
    },
    dragging(value) {
      this.dragging = value
    },
    setWindowInnerHeight(value) {
      this.windowInnerHeight = value
    },
    setMobile(value) {
      this.mobile = value
    },
    // Pinia actions for async logic
    async loadTheme(themeUrl) {
      const theme = await getTheme(themeUrl)
      this.setTheme(theme)
    },
    async getPropositions() {
      try {
        const props = await getPropositions()
        this.setInteractions(props)
      } finally {
        this.propositionsLoaded = true
      }
    },
    loadMessages(mainTheme) {
      getMessages(mainTheme).then((resp) => {
        this.hydrateMessages(resp)
      })
    }
  }
})
